<template>
  <div data-testid="devices-franquias-page">
    <div class="action-buttons">
      <Button data-testid="btn-new-device" :label="t('Novo')" icon="pi pi-plus" @click="handleNewDevice" />
    </div>

    <!-- DevicesFranquias Table -->
    <devices-franquias-table
      ref="devicesFranquiasTableRef"
      @edit-device="handleEditDevice"
      @register-device="handleRegisterDevice">
    </devices-franquias-table>

    <!-- DevicesFranquiasModal is centralized in ModalManager.vue - opened via eventBus -->
    <!-- DeviceRegistrationModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import DevicesFranquiasTable from './DevicesFranquiasTable.vue';
// DevicesFranquiasModal is centralized in ModalManager.vue - opened via eventBus
// DeviceRegistrationModal is centralized in ModalManager.vue - opened via eventBus

type DeviceId = number | string;

interface DeviceRowData {
  device_id: DeviceId;
  device_name?: string;
  [key: string]: unknown;
}

interface DevicesFranquiasTableInstance {
  loadData: () => Promise<void> | void;
}

withDefaults(defineProps<{
  usuarioVersao?: number;
  translations?: Record<string, unknown>;
  datatablesLanguageUrl?: string;
}>(), {
  usuarioVersao: 0,
  translations: () => ({}),
  datatablesLanguageUrl: ''
});

// Get i18n composable
const { t } = useI18n();

// Reactive data
const devicesFranquiasTableRef = ref<DevicesFranquiasTableInstance | null>(null);

// Methods
const loadData = async (): Promise<void> => {
  log('DevicesFranquiasPage', 'loadData', 'Refreshing table data');
  if (devicesFranquiasTableRef.value) {
    await devicesFranquiasTableRef.value.loadData();
  }
};

const handleNewDevice = (): void => {
  log('DevicesFranquiasPage', 'handleNewDevice', 'Opening modal for new record via eventBus');
  eventBus.emit('modal.devices-franquias.open', { action: 'new' });
};

const handleEditDevice = (deviceId: DeviceId): void => {
  log('DevicesFranquiasPage', 'handleEditDevice', 'Opening modal for edit via eventBus', deviceId);
  eventBus.emit('modal.devices-franquias.open', { id: deviceId });
};

const handleEditDeviceEvent = (deviceId?: DeviceId): void => {
  log('DevicesFranquiasPage', 'handleEditDeviceEvent', 'Received edit event', deviceId);
  if (deviceId !== undefined) handleEditDevice(deviceId);
};

const handleRegisterDevice = (rowData: DeviceRowData): void => {
  log('DevicesFranquiasPage', 'handleRegisterDevice', 'Opening device registration modal via eventBus', rowData);
  eventBus.emit('modal.device-registration.open', {
    whatsappfranquiaId: rowData.device_id,
    deviceName: rowData.device_name
  });
};

const handleRegisterDeviceEvent = (rowData?: DeviceRowData): void => {
  log('DevicesFranquiasPage', 'handleRegisterDeviceEvent', 'Received register event', rowData);
  if (rowData !== undefined) handleRegisterDevice(rowData);
};

// Lifecycle
onMounted(() => {
  log('DevicesFranquiasPage', 'mounted', 'Component mounted');

  // Set up eventBus listeners
  eventBus.on('devicesFranquias.new', handleNewDevice);
  eventBus.on('devicesFranquias.edit', handleEditDeviceEvent);
  eventBus.on('devicesFranquias.registerDevice', handleRegisterDeviceEvent);
});

onBeforeUnmount(() => {
  log('DevicesFranquiasPage', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up eventBus listeners
  eventBus.off('devicesFranquias.new', handleNewDevice);
  eventBus.off('devicesFranquias.edit', handleEditDeviceEvent);
  eventBus.off('devicesFranquias.registerDevice', handleRegisterDeviceEvent);
});

// Expose methods for parent components
defineExpose({
  loadData,
  handleNewDevice,
  handleEditDevice,
  handleRegisterDevice
});
</script>

<style scoped>
.action-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}
</style>
